import {useDispatch, useSelector} from 'react-redux';
import {RootState, store} from '../redux/store';
import {
  GAPageInfo,
  GAReportInfo,
  GAAuto,
  TrackGAEventParams,
  GAErrorInfo,
  GAStrInfo,
} from '../types/analyticsTypes';
import {generatePageAndSection, pushToDataLayer} from '../utils/analyticsUtils';
import {
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_PAGE_WITH_SUBPAGE_DATA,
  GA_REGIONS,
  GA_STATIC_URL_MAPPING,
} from '../constants/analyticsConstants';
import {useInRouterContext, useLocation} from 'react-router-dom';
import {setPrevPage, setReportSrc} from '../redux/slices/analyticsSlice';
import {Analytics_Enabled} from '../config';

export function useAnalytics() {
  const userFromStore = useSelector((state: RootState) => state.analytics.user);
  const prevPage = useSelector((state: RootState) => state.analytics.prevPage);
  const reportSrc = useSelector(
    (state: RootState) => state.analytics.report_src
  );
  const inRouter = useInRouterContext();
  const location = inRouter
    ? useLocation()
    : {pathname: window.location.pathname, search: window.location.search};
  const dispatch = useDispatch();

  const buildPagePayload = (page: GAPageInfo): GAPageInfo => {
    const currentPath = location.pathname + location.search;
    const config = GA_PAGE_WITH_SUBPAGE_DATA[currentPath];
    const subpageData =
      typeof config === 'function' ? config(store.getState()) : config;

    return {
      ...page,
      ...(prevPage?.prev_page_name && {
        prev_page_name: prevPage.prev_page_name,
      }),
      ...(prevPage?.prev_page_url && {prev_page_url: prevPage.prev_page_url}),
      ...(subpageData && {
        subpage_name: subpageData.subpage_name,
        subpage_type: subpageData.subpage_type,
      }),
    };
  };

  const resolvePage = (page?: GAPageInfo | GAAuto): GAPageInfo => {
    if (page && page !== GA_DATA_MODE_AUTO) {
      return buildPagePayload(page);
    }

    const mapping = generatePageAndSection(location.pathname + location.search);

    return buildPagePayload({
      name: mapping.page,
      section: mapping.section,
    });
  };

  const resolveUser = (user?: typeof userFromStore | GAAuto) => {
    if (user && user !== GA_DATA_MODE_AUTO) return user;
    return userFromStore;
  };

  const buildReportPayload = (report?: GAReportInfo) => {
    const payload = report
      ? {
          name: report.name,
          type: report.type,
          source: reportSrc || GA_REGIONS.TYPED_OR_BOOKMARKED,
          ...(report.load_time && {load_time: report.load_time}),
        }
      : undefined;
    dispatch(setReportSrc(''));
    return payload;
  };

  const buildStrPayload = (str?: GAStrInfo) =>
    str
      ? {
          name: str.name,
          index: str.index,
          status: str.status,
          ...(str.elapsed_time && {elapsed_time: str.elapsed_time}),
        }
      : undefined;

  const resolveError = (error: GAErrorInfo) => ({
    text: error.text,
    ...(error.code && {code: error.code}),
    ...(error.type && {type: error.type}),
  });

  function trackGAEvent({
    eventName = GA_EVENT_NAMES.PAGE_VIEW,
    page,
    user,
    subPage,
    click,
    error,
    report,
    str,
    file_extension,
    member,
    project,
    hasProjectData,
    pushOnStackOrder = 0,
    files,
  }: TrackGAEventParams) {
    if (!Analytics_Enabled) return;
    pushToDataLayer({
      eventName,
      ...(page && {page: resolvePage(page)}),
      ...(user && {user: resolveUser(user)}),
      ...(click && {click}),
      ...(error && {error: resolveError(error)}),
      ...(report && {report: buildReportPayload(report)}),
      ...(subPage && {subPage}),
      ...(str && {str: buildStrPayload(str)}),
      ...(file_extension && {file_extension}),
      ...(member && {member}),
      ...(project && {project}),
      ...(hasProjectData && {hasProjectData}),
      ...(files && {files}),
      pushOnStackOrder,
    });
    if (eventName == GA_EVENT_NAMES.PAGE_VIEW) {
      const pathname = location.pathname + location.search;
      if (prevPage.prev_page_url !== pathname) {
        dispatch(setPrevPage(GA_STATIC_URL_MAPPING[pathname] || pathname));
      }
    }
  }

  return {
    trackGAEvent,
  };
}
